import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { 
  Feather, 
  Mail, 
  Lock, 
  BookOpen, 
  User, 
  LogOut, 
  ChevronRight, 
  ChevronLeft,
  Check,
  Play,
  Library,
  Heart
} from 'lucide-react';

import { API } from '../config';

export default function SeriesArchive() {
  const { seriesId } = useParams();
  const { user, token, isSubscribed, logout } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [continueReading, setContinueReading] = useState(null);
  const [totalRead, setTotalRead] = useState(0);

  useEffect(() => {
    if (seriesId) {
      fetchSeriesData();
    }
  }, [seriesId, token]);

  const fetchSeriesData = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch series letters - will use cookie auth if no token
      const seriesRes = await axios.get(`${API}/series/${seriesId}/letters`, { 
        headers,
        withCredentials: true 
      });
      
      setSeries(seriesRes.data.series);
      setLetters(seriesRes.data.letters);
      setTotalRead(seriesRes.data.letters_read || 0);
      
      // Try to get continue reading data (may fail if no progress yet)
      try {
        const continueRes = await axios.get(`${API}/progress/continue/${seriesId}`, { 
          headers,
          withCredentials: true 
        });
        setContinueReading(continueRes.data);
      } catch (continueError) {
        console.log('No reading progress yet');
        setContinueReading(null);
      }
    } catch (error) {
      console.error('Error fetching series data:', error);
      // Try to at least get the series info without letters
      try {
        const seriesInfoRes = await axios.get(`${API}/series/${seriesId}`);
        setSeries(seriesInfoRes.data);
        setLetters([]);
      } catch (seriesError) {
        console.error('Error fetching series info:', seriesError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (letterId, isRead) => {
    try {
      await axios.post(
        `${API}/progress/mark-read`,
        { series_id: seriesId, letter_id: letterId, is_read: isRead },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      
      // Update local state
      setLetters(prev => prev.map(l => 
        l.letter_id === letterId ? { ...l, is_read: isRead } : l
      ));
      setTotalRead(prev => isRead ? prev + 1 : prev - 1);
    } catch (error) {
      console.error('Error updating read status:', error);
    }
  };

  const handleToggleFavorite = async (letterId) => {
    try {
      const res = await axios.post(
        `${API}/favorites/toggle`,
        { series_id: seriesId, letter_id: letterId },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      
      // Update local state
      setLetters(prev => prev.map(l => 
        l.letter_id === letterId ? { ...l, is_favorited: res.data.is_favorited } : l
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getLetterTypeLabel = (type) => {
    switch (type) {
      case 'diary': return 'Diary Entry';
      case 'unsent': return 'Unsent Letter';
      case 'interlude': return 'Interlude';
      default: return 'Letter';
    }
  };

  const progressPercentage = series?.total_letters 
    ? (totalRead / series.total_letters) * 100 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="text-center">
          <Feather className="w-8 h-8 text-wine mx-auto mb-4 animate-pulse" />
          <p className="font-lora text-charcoal-muted">Loading series...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-parchment/95 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_ink-nostalgia/artifacts/xhguxwkc_34799.png"
              alt="Unspoken Hearts"
              className="h-10"
            />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/library" 
              className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
              data-testid="library-link"
            >
              <Library className="w-5 h-5" />
              <span className="font-lora hidden sm:inline">Library</span>
            </Link>
            <Link 
              to="/account" 
              className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
              data-testid="account-link"
            >
              <User className="w-5 h-5" />
              <span className="font-lora hidden sm:inline">Account</span>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-charcoal-muted hover:text-charcoal"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/library')}
          className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal mb-6 transition-colors"
          data-testid="back-to-library"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="font-lora">Back to Library</span>
        </button>

        {/* Series Header */}
        {series && (
          <header className="mb-8 animate-fade-in" data-testid="series-header">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <p className="font-lora text-sm text-wine mb-2">{series.era}</p>
                <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mb-3">{series.title}</h1>
                <p className="font-lora text-lg text-charcoal-muted max-w-2xl mb-4">
                  {series.description}
                </p>
                <p className="font-dancing text-lg text-sepia">
                  A story between {series.protagonist_1} & {series.protagonist_2}
                </p>
              </div>

              {/* Progress Card */}
              <Card className="p-5 bg-parchment-paper border-[#E5E0D0] min-w-[240px]" data-testid="progress-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-lora text-sm text-charcoal-muted">Your Progress</span>
                  <span className="font-playfair text-lg text-charcoal">
                    {totalRead}/{series.total_letters}
                  </span>
                </div>
                <Progress value={progressPercentage} className="h-2 mb-4" />
                <p className="font-lora text-xs text-charcoal-muted text-center">
                  {Math.round(progressPercentage)}% complete
                </p>
              </Card>
            </div>
          </header>
        )}

        {/* Continue Reading Button */}
        {continueReading?.next_letter && isSubscribed && (
          <Card 
            className="p-5 mb-8 bg-wine/5 border-wine/20 cursor-pointer hover:bg-wine/10 transition-colors"
            onClick={() => navigate(`/series/${seriesId}/letter/${continueReading.next_letter.week_number}`)}
            data-testid="continue-reading-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-wine/20 rounded-full flex items-center justify-center">
                  <Play className="w-6 h-6 text-wine" />
                </div>
                <div>
                  <p className="font-lora text-sm text-wine">Continue Reading</p>
                  <p className="font-playfair text-lg text-charcoal">
                    Week {continueReading.next_letter.week_number}: {continueReading.next_letter.title}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-wine" />
            </div>
          </Card>
        )}

        {continueReading?.all_read && (
          <Card className="p-5 mb-8 bg-green-50 border-green-200" data-testid="all-read-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-playfair text-lg text-green-800">Series Complete!</p>
                <p className="font-lora text-sm text-green-600">
                  You've read all {series?.total_letters} letters in this series.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Subscribe CTA */}
        {!isSubscribed && (
          <Card className="p-6 mb-8 bg-rose-muted/10 border border-rose-muted/30">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-wine" />
              </div>
              <div>
                <h3 className="font-playfair text-lg text-charcoal mb-1">Subscribe to Unlock</h3>
                <p className="font-lora text-charcoal-muted mb-4">
                  Get access to all 52 letters across all series.
                </p>
                <Button onClick={() => navigate('/subscription')} className="btn-primary" data-testid="subscribe-cta-btn">
                  Subscribe for $7/month
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Letters Grid */}
        <div className="space-y-3" data-testid="letters-list">
          {letters.map((letter, index) => {
            const isAccessible = letter.teaser || isSubscribed;
            
            return (
              <Card
                key={letter.letter_id}
                className={`p-5 transition-all duration-300 ${
                  isAccessible
                    ? 'cursor-pointer bg-parchment border-[#E5E0D0] hover:bg-parchment-paper hover:shadow-soft-paper hover:border-sepia/30'
                    : 'bg-parchment-subtle/50 border-[#E5E0D0]/50'
                }`}
                style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                data-testid={`letter-card-${letter.week_number}`}
              >
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 flex-1"
                    onClick={() => isAccessible && navigate(`/series/${seriesId}/letter/${letter.week_number}`)}
                  >
                    {/* Week Number / Read Status */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      letter.is_read 
                        ? 'bg-green-100 border-2 border-green-400' 
                        : 'bg-parchment-paper border border-[#E5E0D0]'
                    }`}>
                      {letter.is_read ? (
                        <Check className="w-6 h-6 text-green-600" />
                      ) : (
                        <span className="font-playfair text-lg text-charcoal">
                          {String(letter.week_number).padStart(2, '0')}
                        </span>
                      )}
                    </div>

                    {/* Letter Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-lora text-sm text-charcoal-muted">Week {letter.week_number}</span>
                        {letter.teaser && (
                          <span className="text-xs font-lora text-wine bg-rose-muted/20 px-2 py-0.5 rounded-full">
                            Free
                          </span>
                        )}
                      </div>
                      <h3 className={`font-playfair text-lg mb-1 truncate ${
                        isAccessible ? 'text-charcoal' : 'text-charcoal-muted'
                      }`}>
                        {letter.title}
                      </h3>
                      <div className="flex items-center gap-3">
                        <span className={`font-dancing text-sm ${
                          isAccessible ? 'text-sepia' : 'text-charcoal-light'
                        }`}>
                          {letter.date}
                        </span>
                        {letter.pov && (
                          <span className="font-lora text-xs text-charcoal-light">
                            {letter.pov} · {getLetterTypeLabel(letter.letter_type)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {isAccessible ? (
                      <>
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(letter.letter_id);
                          }}
                          className={`p-2 rounded-full transition-colors ${
                            letter.is_favorited 
                              ? 'bg-rose-100 text-rose-500 hover:bg-rose-200' 
                              : 'bg-parchment-paper text-charcoal-light hover:bg-parchment-subtle hover:text-rose-400'
                          }`}
                          title={letter.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                          data-testid={`favorite-btn-${letter.week_number}`}
                        >
                          <Heart className={`w-5 h-5 ${letter.is_favorited ? 'fill-current' : ''}`} />
                        </button>
                        {/* Mark as Read/Unread Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(letter.letter_id, !letter.is_read);
                          }}
                          className={`p-2 rounded-full transition-colors ${
                            letter.is_read 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-parchment-paper text-charcoal-light hover:bg-parchment-subtle hover:text-charcoal'
                          }`}
                          title={letter.is_read ? 'Mark as unread' : 'Mark as read'}
                          data-testid={`mark-read-btn-${letter.week_number}`}
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <ChevronRight className="w-5 h-5 text-charcoal-light" />
                      </>
                    ) : (
                      <Lock className="w-5 h-5 text-charcoal-light" />
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Show all 52 weeks placeholder for non-subscribers */}
        {!isSubscribed && letters.length < 52 && (
          <div className="mt-6 text-center">
            <p className="font-lora text-charcoal-muted mb-4">
              + {52 - letters.length} more letters available with subscription
            </p>
            <Button onClick={() => navigate('/subscription')} className="btn-primary">
              Unlock All Letters
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E0D0] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="font-dancing text-lg text-sepia">"Moments preserved in ink"</p>
          <p className="font-lora text-sm text-charcoal-muted">© 2026 Unspoken Hearts</p>
        </div>
      </footer>
    </div>
  );
}
